import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Spinner,
  OffcanvasBody,
  Offcanvas,
  OffcanvasHeader,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import CustomerMetaFieldService from "../../../services/OrderingServices/CustomerMetaFieldService";

// Validation Schema
const validationSchema = Yup.object({
  code: Yup.string().required("Code is required"),
  title: Yup.string().required("Title is required"),
  description: Yup.string(),
  isActive: Yup.boolean(),
});

const CustomerMetaFieldForm = ({ selectedUnit, modal, toggleModal, fetchUnit }) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: selectedUnit?.id || "",
      code: selectedUnit?.code || "",
      title: selectedUnit?.title || "",
      description: selectedUnit?.description || "",
      isActive: selectedUnit?.isActive ?? true,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);

        if (values.id) {
          await CustomerMetaFieldService.update(values);
          toast.success("Customer meta field updated successfully.");
        } else {
          const { id, ...createPayload } = values;
          await CustomerMetaFieldService.create(createPayload);
          toast.success("New customer meta field added successfully.");
          resetForm();
        }
        fetchUnit();
        toggleModal();
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!modal) {
      formik.resetForm();
    }
  }, [modal]);

  return (
    <Offcanvas
      isOpen={modal}
      toggle={toggleModal}
      direction="end"
      backdrop={true}
      scrollable
      style={{ width: "420px" }}
    >
      {/* HEADER */}
      <OffcanvasHeader toggle={toggleModal} className="border-bottom">
        <i className="ri-file-list-3-line me-2"></i> Customer Meta Field Form
      </OffcanvasHeader>

      {/* WRAPPER FORM */}
      <Form
        onSubmit={formik.handleSubmit}
        className="d-flex flex-column flex-grow-1"
      >
        <OffcanvasBody className="d-flex flex-column p-3">
          {/* SCROLLABLE CONTENT */}
          <div className="flex-grow-1 overflow-auto">
            {/* Code */}
            <div className="mb-3">
              <Label className="fw-bold mb-2">Code</Label>
              <Input
                id="code"
                name="code"
                type="text"
                className="form-control shadow-sm"
                placeholder="Enter code"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.code}
                invalid={!!(formik.touched.code && formik.errors.code)}
              />
              {formik.touched.code && formik.errors.code && (
                <FormFeedback type="invalid">{formik.errors.code}</FormFeedback>
              )}
            </div>

            {/* Title */}
            <div className="mb-3">
              <Label className="fw-bold mb-2">Title</Label>
              <Input
                id="title"
                name="title"
                type="text"
                className="form-control shadow-sm"
                placeholder="Enter title"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
                invalid={!!(formik.touched.title && formik.errors.title)}
              />
              {formik.touched.title && formik.errors.title && (
                <FormFeedback type="invalid">
                  {formik.errors.title}
                </FormFeedback>
              )}
            </div>

            {/* Description */}
            <div className="mb-3">
              <Label className="fw-bold mb-2">Description</Label>
              <textarea
                id="description"
                name="description"
                rows={5}
                className={`form-control shadow-sm ${
                  formik.touched.description && formik.errors.description
                    ? "is-invalid"
                    : ""
                }`}
                placeholder="Enter description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                style={{ padding: "1rem" }}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="invalid-feedback">
                  {formik.errors.description}
                </div>
              )}
            </div>

            {/* Active Status Toggle */}
            <div className="mb-3 d-flex align-items-center justify-content-between">
              <Label className="fw-bold mb-0">Active Status</Label>
              <div className="form-check form-switch">
                <Input
                  type="switch"
                  id="isActive"
                  name="isActive"
                  className="form-check-input"
                  checked={formik.values.isActive}
                  onChange={formik.handleChange}
                />
              </div>
            </div>
          </div>

          {/* FOOTER BUTTONS (Sticky) */}
          <div
            className="bg-white border-top pt-3 pb-3 d-flex justify-content-between gap-2 position-sticky"
            style={{ bottom: 0, left: 0 }}
          >
            <Button
              color="danger"
              onClick={toggleModal}
              className="w-50 border shadow-sm"
            >
              Cancel
            </Button>

            <Button
              color="primary"
              type="submit"
              className="w-50 shadow-sm"
              disabled={loading}
            >
              {loading && <Spinner size="sm" className="me-2" />}
              {loading
                ? selectedUnit?.id
                  ? "Updating..."
                  : "Creating..."
                : selectedUnit?.id
                ? "Update"
                : "Create"}
            </Button>
          </div>
        </OffcanvasBody>
      </Form>
    </Offcanvas>
  );
};

export default CustomerMetaFieldForm;
